import { styles } from 'components/common/Text'

export const globalStyles = (theme) => ({
  ':root': {
    '--header-height': '72px',
    '--info-banner-height': '0px',
    // Tells @reach we're providing our own styles and suppresses console warning
    '--reach-menu-button': 1,
    '--reach-portal': 1
  },

  '*': {
    margin: 0,
    padding: 0,
    outline: 0,
    boxSizing: 'border-box'
  },

  // 1. Normalize default `font-family`
  // 2. Prevents iOS text size adjust after orientation change, without disabling user zoom
  // 3. Setting @viewport causes scrollbars to overlap content in IE11 and Edge, so we force a non-overlapping, non-auto-hiding scrollbar to counteract.
  html: {
    // 1
    height: '100%',
    fontFamily: theme.fonts.base,
    fontWeight: theme.fontWeights[0],
    color: theme.colors.steel[2],
    // 2
    textSizeAdjust: '100%',
    // 3
    msOverflowStyle: 'scrollbar',
    WebkitFontSmoothing: 'antialiased', // Standardize font-rendering "boldness" in browsers
    MozOsxFontSmoothing: 'grayscale' // Standardize font-rendering "boldness" in browsers
  },

  body: {
    minHeight: '100%'
  },

  // Remove the gap between audio, canvas, iframes, images, videos and the bottom of their containers.
  // @NOTE Commenting the original (leaving as reference for later) out as our icon alignment is all over the place with SVGs included
  /*
  'audio, canvas, iframe, img, svg, video': {
    verticalAlign: 'middle'
  },
  */

  // 1. Remove border in all browsers
  // 2. Remove gap between iframes and the bottom of its container
  iframe: {
    border: 0,
    verticalAlign: 'middle'
  },

  a: {
    cursor: 'pointer',
    color: theme.colors.blue[0],
    transition: 'color 150ms',
    textDecoration: 'none',

    '&:hover': {
      color: theme.colors.blue[0]
    }
  },

  // 1. Add correct box sizing and heigh tin Firefox
  // 2. Show the overflow in Edge and IE.
  // 3. Add the correct text-align in Edge and IE.
  // 4. Style
  hr: {
    // 1
    boxSizing: 'content-box',
    height: 0,
    // 2
    overflow: 'visible',
    // 3
    textAlign: 'inherit',
    // 4
    marginTop: theme.space[3],
    marginBottom: theme.space[3],
    border: 0,
    borderTop: theme.border.base
  },

  h1: styles.h1,

  h2: styles.h2,

  h3: styles.h3,

  h4: styles.h4,

  '.offset, .divider': {
    color: theme.colors.steel[1]
  },

  '.divider': {
    paddingBottom: '0.75rem',
    borderBottom: theme.border.base,
    display: 'flex'
  },

  '.right': {
    marginLeft: 'auto'
  },

  'code, pre': {
    fontFamily: theme.fonts.mono,
    fontSize: '0.9rem'
  },

  'strong, b': {
    fontWeight: theme.fontWeights[1]
  },

  //
  // Form
  //

  'label.checkbox': {
    display: 'flex',
    alignItems: 'center',

    'input[type="checkbox"]': {
      marginRight: '0.5rem'
    }
  },

  'form > header': {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',

    h1: {
      flex: 1
    },

    '.right': {
      flex: '1',
      marginLeft: 'auto',
      width: '50%',
      textAlign: 'right'
    },

    '.description': {
      marginTop: '1rem',
      width: '100%'
    }
  },

  '.field': {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '1rem',

    '.input, .detail': {
      width: '100%'
    },

    '.value': {
      width: '100%'
    },

    'label, .label': {
      display: 'block',
      minWidth: '12rem',
      marginRight: '0.5rem'
    }
  },

  'input.switch': {
    opacity: 0,
    position: 'absolute',

    '+ label': {
      display: 'block',
      width: '4rem',
      position: 'relative',
      cursor: 'pointer',

      '&::before': {
        content: "''",
        display: 'block',
        borderRadius: '2rem',
        height: '2rem',
        width: '3.5rem',
        backgroundColor: theme.colors.border[0],
        transition: 'background-color 150ms, border-color 150ms'
      },

      '&::after': {
        content: "''",
        borderRadius: '1.25rem',
        height: '1.5rem',
        width: '1.5rem',
        position: 'absolute',
        top: '50%',
        transform: 'translateX(0.25rem) translateY(-50%)',
        transition: 'transform 150ms, border-color 150ms',
        backgroundColor: 'white'
      }
    },

    '&:checked + label': {
      '&::before': {
        backgroundColor: theme.colors.green[0]
      },
      '&::after': {
        transform: 'translateX(1.75rem) translateY(-50%)'
      }
    }
  },

  //
  // Profile
  //

  '#Profile': {
    '.menu': {
      display: 'flex',
      alignItems: 'center',

      a: {
        marginLeft: '1rem'
      }
    },

    '.green:not(button)': {
      color: theme.colors.green[0],
      fontWeight: theme.fontWeights[1]
    },

    '.red:not(button)': {
      color: theme.colors.red[1],
      fontWeight: theme.fontWeights[1]
    }
  },

  '.ReactModal__Overlay': {
    zIndex: '3000',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    position: 'fixed',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0
  },

  '.ReactModal__Content': {
    backgroundColor: theme.colors.white[0],
    boxShadow: theme.boxShadows.large,
    borderRadius: theme.borderRadius[1],
    width: '40rem',
    minHeight: '5rem',
    margin: 'auto',
    marginTop: '3.5rem',
    padding: '1.5rem 2rem'
  }
})

export const fonts = `
  @font-face {
    font-family: 'Sohne VF';
    src: local(😜), url('/public/fonts/SohneVF-web.woff2') format('woff2');
    font-style: normal;
    font-variation-ligatures: normal;
    font-weight: 400 600;
  }

  @font-face {
    font-family: 'Sohne Mono';
    src: local(😜), url('/public/fonts/SohneMono-light.woff2') format('woff2');
    font-weight: 300;
    font-style: normal;
  }
`
